"use client";


import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type RefObject } from "react";
import { cn } from "@/lib/utils";

const delayClassMap = {
  0: "delay-0",
  10: "delay-100",
  15: "delay-150",
  20: "delay-200",
  30: "delay-300",
} as const;

function getDelayClass(delay: number) {
  return delayClassMap[Math.round(delay * 100) as keyof typeof delayClassMap] ?? "delay-0";
}

function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible] as const;
}

function Reveal({
  children,
  delay = 0,
  from = "bottom",
}: {
  children: React.ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, visible] = useVisible();
  const fromClasses = {
    bottom: "translate-y-8",
    left: "-translate-x-8",
    right: "translate-x-8",
  } as const;

  return (
    <div
      ref={ref}
      className={cn(
        "opacity-0",
        fromClasses[from],
        "transition-all duration-[650ms] ease-out",
        getDelayClass(delay),
        visible && "opacity-100 translate-x-0 translate-y-0"
      )}
    >
      {children}
    </div>
  );
}

function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5 block",
        className
      )}
    >
      {children}
    </span>
  );
}

function Divider({ className }: { className?: string }) {
  return <div className={cn("w-11 h-[3px] bg-[#1565C0] mb-6", className)} />;
}

type Service = {
  num: string;
  title: string;
  tagline: string;
  body: string;
  items: string[];
  img: string;
  href: string;
};

const SERVICES: Service[] = [
  {
    num: "01",
    title: "Business Consultancy",
    tagline: "Strategy, operations and growth planning from experts.",
    body: "We analyse your market position, identify bottlenecks and build a custom growth roadmap. Expert guidance for businesses at every stage.",
    items: [
      "Strategic business planning and roadmapping",
      "Market research and competitor analysis",
      "Operations and process optimization",
      "Business model restructuring",
      "Investor pitch preparation",
    ],
    img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80",
    href: "/services/consultancy",
  },
  {
    num: "02",
    title: "Funding Assistance",
    tagline: "Access the right capital at the right time.",
    body: "Expert guidance across all funding avenues — grants, loans, NBFC financing, VC funding and government schemes tailored for your business.",
    items: [
      "Government grants (non-repayable funding)",
      "Business loans — term, working capital, MSME",
      "NBFC financing — fast approval, flexible eligibility",
      "Venture capital and angel investor introductions",
      "Debt restructuring and refinancing",
    ],
    img: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80",
    href: "/services/funding",
  },
  {
    num: "03",
    title: "Certification Assistance",
    tagline: "Get recognized. Get benefits.",
    body: "We handle the entire certification process — from eligibility assessment to portal submission and post-registration compliance management.",
    items: [
      "MSME / Udyam Registration",
      "Startup India (DPIIT Recognition)",
      "ISO Certification — 9001, 14001, 27001",
      "ZED (Zero Defect Zero Effect) Certification",
      "FSSAI, NSIC, GeM and sector-specific registrations",
    ],
    img: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&q=80",
    href: "/services/certification",
  },
  {
    num: "04",
    title: "Legal Assistance",
    tagline: "Protect your business with robust legal frameworks.",
    body: "Proactive compliance management and legal protection designed for the Indian regulatory environment. From incorporation to governance.",
    items: [
      "Company incorporation and statutory registrations",
      "Contract drafting, review and legal vetting",
      "GST registration, filing and compliance",
      "Labour law advisory and HR compliance",
      "Corporate governance and secretarial services",
    ],
    img: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80",
    href: "/services/legal",
  },
  {
    num: "05",
    title: "Marketing Assistance",
    tagline: "Grow your brand. Acquire customers. Dominate your market.",
    body: "360° digital marketing strategies built for Indian MSMEs and startups — combining performance campaigns, SEO and automation.",
    items: [
      "Search Engine Optimization (SEO)",
      "Performance marketing — Google Ads, Meta Ads",
      "Social media management and growth",
      "WhatsApp Business marketing and automation",
      "Website development and conversion optimization",
    ],
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    href: "/services/marketing",
  },
  {
    num: "06",
    title: "IT Solutions",
    tagline: "End-to-end technology services for modern enterprises.",
    body: "Custom software, mobile apps, cloud infrastructure, cybersecurity, AI/ML and enterprise ERP/CRM systems built to scale.",
    items: [
      "Custom software and web application development",
      "Mobile app development — iOS android, cross-platform",
      "Cloud migration, infrastructure and managed services",
      "ERP and CRM systems (Enterprise & Customer Maximiser)",
      "Cybersecurity, AI/ML and DevOps services",
    ],
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
    href: "/services/it",
  },
];

function ServiceCard({ s, i }: { s: Service; i: number }) {
  const [ref, visible] = useVisible();

  return (
    <div
      ref={ref}
      className={cn(
        "group flex flex-col overflow-hidden rounded-sm border border-[#E2E8F0] bg-white shadow-sm transition-all duration-500 ease-out hover:border-[#CBD5E1] hover:shadow-[0_16px_48px_rgba(0,0,0,0.09)]",
        getDelayClass((i % 3) * 0.12),
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="relative h-[200px] overflow-hidden">
        <Image src={s.img} alt={s.title} fill className="object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
        <div className="absolute top-4 left-4 rounded-sm bg-[#1565C0] px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          {s.num}
        </div>
      </div>

      <div className="flex flex-col p-[28px] pb-[32px]">
        <h3 className="text-[18px] font-bold text-[#0F172A] mb-2 leading-[1.25]">{s.title}</h3>
        <p className="text-[13px] font-medium text-[#1565C0] mb-3.5 leading-[1.5]">{s.tagline}</p>
        <p className="text-[13.5px] text-[#64748B] mb-5 leading-[1.78]">{s.body}</p>

        <ul className="mb-7 flex flex-col gap-2">
          {s.items.map((item, j) => (
            <li key={j} className="flex items-start gap-2">
              <span className="mt-[6px] block h-2 w-2 rounded-full bg-[#1565C0] flex-shrink-0" />
              <span className="text-[13px] text-[#334155] leading-[1.6]">{item}</span>
            </li>
          ))}
        </ul>

        <Link href={s.href} className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.04em] text-[#1565C0] transition-colors duration-200 hover:text-[#0D47A1]">
          <span>LEARN MORE</span>
          <svg className="transition-transform duration-200 group-hover:translate-x-1" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function StepItem({ num, title, body, delay }: { num: string; title: string; body: string; delay: number }) {
  const [ref, visible] = useVisible();

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-start transition-all duration-[550ms] ease-out",
        getDelayClass(Math.round(delay * 100)),
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
      )}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1565C0] text-[14px] font-bold text-white mb-4">
        {num}
      </div>
      <h4 className="text-[15px] font-semibold text-[#0F172A] mb-2">{title}</h4>
      <p className="text-[13.5px] leading-[1.75] text-[#64748B]">{body}</p>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <main className="bg-white">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">
        <div className="flex flex-col justify-center bg-white py-8 lg:py-[30px] px-4 sm:px-8 lg:pl-[80px] lg:pr-[30px]">
          <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#1565C0] mb-3.5">Our Services</span>
          <Divider />
          <h1 className="text-[clamp(28px,3.2vw,44px)] font-extrabold text-[#0F172A] leading-[1.12] mb-5 ">
            Comprehensive Business & IT Solutions —<br />All Under One Roof
          </h1>
          <p className="text-[16px] font-light leading-[1.85] text-[#64748B]  mb-10">
            From your first business registration to your 100th crore in revenue — Farsight Solutions has the expertise, tools and team to support every stage of your business journey.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="rounded-sm bg-[#1565C0] px-7 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
              GET STARTED
            </button>
            <button className="rounded-sm border border-[#E2E8F0] bg-white px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
              CONTACT US
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden w-full min-h-[250px] sm:min-h-[340px] lg:min-h-[420px]">
          <Image
            src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1000&q=85"
            alt="Farsight services"
            fill
            className="object-cover"
          />
        </div>
      </section>

      <div className="bg-[#1565C0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { v: "6", l: "Service Categories" },
          { v: "30+", l: "Services Offered" },
          { v: "100+", l: "Clients Served" },
          { v: "₹50Cr+", l: "Funds Facilitated" },
        ].map((item, index) => (
          <div key={index} className={cn("py-5 text-center", index < 3 ? "xl:border-r border-white/15" : "")}> 
            <div className="text-[24px] font-black text-white leading-none">{item.v}</div>
            <div className="text-[11px] uppercase tracking-[0.08em] text-white/70 mt-1">{item.l}</div>
          </div>
        ))}
      </div>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="mb-8 sm:mb-10 lg:mb-[60px]">
              <Eyebrow>What We Offer</Eyebrow>
              <Divider />
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-[#0F172A] leading-[1.18] max-w-[640px]">
                  Six Practice Areas.<br />One Trusted Partner.
                </h2>
                <p className="text-[14px] leading-[1.75] text-[#64748B] max-w-[360px]">
                  Each service area is staffed by specialists who understand the Indian business environment inside out.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-3">
            {SERVICES.map((service, index) => (
              <ServiceCard key={service.num} s={service} i={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1200px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div className="relative w-full h-[250px] sm:h-[340px] lg:h-[420px]">
              <Image
                src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
                alt="Why Farsight"
                fill
                className="object-cover"
              />
              <div className="absolute -bottom-6 -right-6 rounded-sm border border-[#E2E8F0] bg-white p-[24px] shadow-[0_8px_32px_rgba(0,0,0,0.10)] min-w-[200px]">
                <div className="text-[34px] font-black text-[#1565C0] leading-none">100+</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Clients Served</div>
                <div className="mt-4 text-[32px] font-black text-[#E65100] leading-none">98%</div>
                <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.06em] text-[#64748B]">Success Rate</div>
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div>
              <Eyebrow>Why Farsight</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(24px,2.8vw,36px)] font-bold text-[#0F172A] leading-[1.18] mb-5">
                Integrated Expertise.<br />Measurable Outcomes.
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-8">
                Unlike standalone consultants or single-service agencies, Farsight brings together business strategy, legal expertise, technology and marketing under one roof — giving you a seamless experience with no gaps or hand-off failures.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  "One partner for all your business needs — no juggling vendors",
                  "Deep expertise in Indian regulatory and financial environment",
                  "Transparent pricing, no hidden fees, milestone-based delivery",
                  "Dedicated relationship manager for every client account",
                ].map((point, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#1565C0] text-white">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="text-[14px] text-[#334155] leading-[1.7]">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-[#F8FAFC]">
        <div className="max-w-[1200px] mx-auto">
          <Reveal>
            <div className="text-center mb-8 sm:mb-12 lg:mb-[64px]">
              <Eyebrow className="mb-2">Our Process</Eyebrow>
              <Divider className="mx-auto mb-6" />
              <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-[#0F172A] leading-[1.18] max-w-[760px] mx-auto">
                How We Work With You
              </h2>
            </div>
          </Reveal>

          <div className="grid gap-8 sm:gap-10 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { num: "01", title: "Discovery Call", body: "We start with a free consultation to understand your business, goals and current challenges without any obligation." },
              { num: "02", title: "Custom Proposal", body: "Our team prepares a tailored service proposal with a clear scope, timeline and transparent pricing for your review." },
              { num: "03", title: "Execution", body: "Our specialists execute the agreed plan with regular progress updates, milestone reporting and proactive communication." },
              { num: "04", title: "Review & Support", body: "Post-delivery, we provide comprehensive handover, training where needed and ongoing support to ensure lasting results." },
            ].map((step, index) => (
              <StepItem key={step.num} num={step.num} title={step.title} body={step.body} delay={index * 0.1} />
            ))}
          </div>

          <div className="relative mt-[-30px] mb-14 pointer-events-none opacity-20">
            <div className="absolute left-[12.5%] right-[12.5%] top-5 h-px bg-[#1565C0]" />
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 lg:py-[60px] px-4 sm:px-8 lg:px-[80px] bg-white">
        <div className="max-w-[1200px] mx-auto grid gap-8 lg:gap-20 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal from="left">
            <div>
              <Eyebrow>Get Started</Eyebrow>
              <Divider />
              <h2 className="text-[clamp(26px,3vw,40px)] font-bold text-[#0F172A] leading-[1.15] mb-4">
                Choose Your Path<br />to Success
              </h2>
              <p className="text-[15px] leading-[1.85] text-[#64748B] mb-9">
                Each service is tailored to your specific business needs. Book a free discovery call and let our experts identify the right solution for you — at no obligation.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="rounded-sm bg-[#1565C0] px-8 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white transition-colors duration-200 hover:bg-[#12579f]">
                  BOOK A FREE CALL
                </button>
                <button className="rounded-sm border border-[#E2E8F0] bg-white px-8 py-3 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC]">
                  CONTACT US
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative w-full h-[360px] overflow-hidden rounded-sm border border-[#E2E8F0]">
              <Image
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1400&q=80"
                alt="Book a free call"
                fill
                className="object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
